import {getMarket} from '../utils/market';
import {t, useLanguage, interfaceLocale} from '../i18n';
import {usePendingEditor} from "../hooks/useEditorNavigation";
// src/sections/LoyaltySection.jsx
// LOOP D1.1 — Loyalty Programs UI + Data Model
// Premium revamp: dashboard + right side sheet for create/edit.

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  collection,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { callCallableNativeSafe } from "../utils/callableNativeSafe";
import { db as defaultDb } from "../firebase";

function clean(value) {
  return String(value || "").trim();
}

function asNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function safeServices(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function serviceLabel(service = {}) {
  return clean(service.name || service.title || service.serviceName || service.label || "Servicio");
}

function defaultDraft() {
  return {
    name: "",
    description: "",
    type: "stamp",
    stampsRequired: "5",
    stampsPerPaidBooking: "1",
    pointsRequired: "100",
    pointsPerDollar: "1",
    pointsPerPaidBooking: "0",
    pointManualStep: "10",
    rewardLabel: "",
    eligibleMode: "all",
    eligibleServiceIds: [],
    active: true,
  };
}

function normalizeProgram(id, data = {}) {
  const eligibleServiceIds = Array.isArray(data.eligibleServiceIds) ? data.eligibleServiceIds.map(String).filter(Boolean) : [];
  const rawType = clean(data.type || data.programType || data.loyaltyType || "stamp").toLowerCase();
  const type = rawType === "points" || rawType === "puntos" ? "points" : "stamp";
  return {
    id,
    name: clean(data.name || (type === "points" ? "Programa de puntos" : "Programa de sellos")),
    description: clean(data.description || ""),
    type,
    active: data.active !== false,
    stampsRequired: Math.max(1, asNumber(data.stampsRequired, 5)),
    stampsPerPaidBooking: Math.max(1, asNumber(data.stampsPerPaidBooking, 1)),
    pointsRequired: Math.max(1, asNumber(data.pointsRequired, 100)),
    pointsPerDollar: Math.max(0, asNumber(data.pointsPerDollar, 1)),
    pointsPerPaidBooking: Math.max(0, asNumber(data.pointsPerPaidBooking, 0)),
    pointManualStep: Math.max(1, asNumber(data.pointManualStep, 10)),
    eligibleMode: data.eligibleMode === "selected" || eligibleServiceIds.length ? "selected" : "all",
    eligibleServiceIds,
    rewardType: clean(data.rewardType || "custom"),
    rewardLabel: clean(data.rewardLabel || "Recompensa"),
    earnTrigger: clean(data.earnTrigger || "payment_paid"),
    redeemMode: clean(data.redeemMode || "manual"),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

function programSummary(program, services = [], currency = 'USD') {
  const count = program.eligibleServiceIds.length;
  const eligible = program.eligibleMode === 'selected' ? t('{count} servicios', {count}) : t('Todos los servicios');
  const unit = new Intl.NumberFormat(interfaceLocale(), {style: 'currency', currency, currencyDisplay: 'code'}).format(1);
  const earn = program.type === 'points'
    ? [program.pointsPerDollar > 0 ? t('{count} puntos por {unit}', {count: program.pointsPerDollar, unit}) : '',
       program.pointsPerPaidBooking > 0 ? t('{count} puntos fijos por pago', {count: program.pointsPerPaidBooking}) : ''].filter(Boolean).join(' + ') || t('Puntos por pago')
    : t('{count} sellos por cita completada y pagada', {count: program.stampsPerPaidBooking});
  const goal = program.type === 'points' ? t('{count} puntos para recompensa', {count: program.pointsRequired}) : t('{count} para recompensa', {count: program.stampsRequired});
  return `${earn} · ${goal} · ${eligible}`;
}
function programEarnText(program, currency) { return programSummary(program, [], currency).split(' · ')[0]; }
function programGoalText(program) {
  return program.type === 'points' ? t('{count} puntos', {count: program.pointsRequired}) : t('{count} sellos', {count: program.stampsRequired});
}

function Icon({ name }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.15,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };
  const paths = {
    star: <path d="m12 3 2.7 5.47 6.03.88-4.36 4.25 1.03 6-5.4-2.84-5.4 2.84 1.03-6-4.36-4.25 6.03-.88L12 3Z" />,
    diamond: <path d="M6 3h12l4 6-10 12L2 9l4-6Z" />,
    chart: <><path d="M4 19V5" /><path d="M8 19v-7" /><path d="M12 19V8" /><path d="M16 19v-4" /><path d="M20 19V9" /></>,
    gift: <><path d="M20 12v8H4v-8" /><path d="M2 8h20v4H2z" /><path d="M12 8v12" /><path d="M12 8H7.5a2.5 2.5 0 1 1 2.2-3.7L12 8Z" /><path d="M12 8h4.5a2.5 2.5 0 1 0-2.2-3.7L12 8Z" /></>,
    percent: <><path d="m19 5-14 14" /><circle cx="7" cy="7" r="2" /><circle cx="17" cy="17" r="2" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    points: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="2" /></>,
    users: <><path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" /><circle cx="9.5" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
    redeem: <><path d="M12 3v18" /><path d="M17 7H9.5a2.5 2.5 0 0 0 0 5H14a2.5 2.5 0 0 1 0 5H6" /></>,
    more: <><circle cx="12" cy="5" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /><circle cx="12" cy="19" r="1" fill="currentColor" stroke="none" /></>,
    trophy: <><path d="M8 21h8" /><path d="M12 17v4" /><path d="M7 4h10v5a5 5 0 0 1-10 0V4Z" /><path d="M5 5H3v2a4 4 0 0 0 4 4" /><path d="M19 5h2v2a4 4 0 0 1-4 4" /></>,
    close: <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>,
    chevron: <path d="m9 18 6-6-6-6" />,
  };
  return <svg {...common}>{paths[name] || paths.star}</svg>;
}

function Field({ label, help, children }) {
  return (
    <label className="loyalty-field">
      <span>{label}</span>
      {children}
      {help ? <small>{help}</small> : null}
    </label>
  );
}

function MetricCard({ icon, value, label }) {
  return (
    <div className="loyalty-metric-card">
      <span className="loyalty-metric-icon"><Icon name={icon} /></span>
      <div>
        <strong>{value}</strong>
        <em>{label}</em>
      </div>
    </div>
  );
}

function fmtMetric(value) {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString(interfaceLocale());
}

function fmtEventDate(value) {
  try {
    if (!value) return "Ahora";
    const d = value.seconds ? new Date(value.seconds * 1000) : new Date(value);
    if (!d || Number.isNaN(d.getTime())) return "Ahora";
    return d.toLocaleString(interfaceLocale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "Ahora";
  }
}

function RecentLoyaltyEvent({ event }) {
  const tone = event?.type === "redeem" ? "redeem" : event?.type === "reversal" ? "reversal" : "earn";
  const icon = tone === "redeem" ? "gift" : tone === "reversal" ? "redeem" : "star";
  return (
    <div className={`loyalty-event-row is-${tone}`}>
      <span><Icon name={icon} /></span>
      <div>
        <strong>{event?.customerName || "Cliente"}</strong>
        <small>{event?.label || "Actividad de fidelidad"}{event?.programName ? ` · ${event.programName}` : ""}</small>
      </div>
      <em>{fmtEventDate(event?.createdAtIso || event?.createdAt)}</em>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="loyalty-empty">
      <div className="loyalty-empty-icon"><Icon name="gift" /></div>
      <h3>{t("Empieza con una tarjeta de fidelidad")}</h3>
      <p>{t("Crea una regla simple para que tus clientes ganen sellos o puntos cuando paguen reservas elegibles.")}</p>
      <button type="button" onClick={onCreate}><Icon name="plus" />{" " + t("Crear programa")}</button>
    </div>
  );
}

function LoyaltySideSheet({ children, onClose }) {
  const node = (
    <div className="loyalty-overlay" onMouseDown={onClose}>
      <aside className="loyalty-sheet" onMouseDown={(e) => e.stopPropagation()}>
        {children}
      </aside>
    </div>
  );
  if (typeof document !== "undefined" && document.body) return createPortal(node, document.body);
  return node;
}

export default function LoyaltySection({ businessId, db: dbProp, businessDoc = {}, services = [] }) {
  const language = useLanguage();
  const currency = businessDoc.currency || getMarket(businessDoc.countryCode).currency;
  const currencyUnit = new Intl.NumberFormat(interfaceLocale(), {style: "currency", currency, currencyDisplay: "code"}).format(1);
  const db = dbProp || defaultDb;
  const [programs, setPrograms] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [overviewError, setOverviewError] = useState("");
  const requestId = useRef(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [draftOpen, setDraftOpen] = useState(false);
  usePendingEditor({pending:draftOpen,busy:saving,title:"Fidelidad",discard:()=>setDraftOpen(false)});
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(defaultDraft);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const serviceOptions = useMemo(() => safeServices(services).map((service, index) => ({
    id: clean(service.id || service.serviceId || service.uid || `service-${index}`),
    label: serviceLabel(service),
  })).filter((x) => x.id && x.label), [services]);

  const activePrograms = programs.filter((p) => p.active).length;
  const overviewSnapshot = overview?.snapshot || {};
  const recentEvents = Array.isArray(overview?.recentEvents) ? overview.recentEvents : [];
  const topCustomers = Array.isArray(overview?.topCustomers) ? overview.topCustomers : [];
  const visiblePrograms = useMemo(() => {
    const q = clean(search).toLowerCase();
    return programs.filter((program) => {
      if (statusFilter === "active" && !program.active) return false;
      if (statusFilter === "paused" && program.active) return false;
      if (statusFilter === "points" && program.type !== "points") return false;
      if (statusFilter === "stamp" && program.type !== "stamp") return false;
      if (!q) return true;
      const haystack = [program.name, program.description, program.rewardLabel, programSummary(program, services, currency)].join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [programs, search, statusFilter, services, language, currency]);

  const callLoyaltyFunction = async (name, payload = {}) => {
    const res = await callCallableNativeSafe({ functionName: name, data: { businessId, ...payload }, timeoutMs: 60000 });
    return res && res.data ? res.data : {};
  };

  const loadPrograms = async () => {
    if (!db || !businessId) return;
    const id = ++requestId.current;
    setLoading(true);
    setError("");
    setOverviewLoading(true);
    setOverviewError("");
    (async () => {
      let cursor = "", combined = null;
      do {
        const page = await callLoyaltyFunction("getBusinessLoyaltyOverview", { eventLimit: 18, cursor, pagination: true });
        if (id !== requestId.current) return;
        if (!combined) combined = page;
        else {
          for (const key of ["customersWithLoyalty", "totalRewardsAvailable", "totalStampsBalance", "totalPointsBalance", "lifetimeRewardsRedeemed"]) combined.snapshot[key] += page.snapshot[key];
          combined.topCustomers = [...combined.topCustomers, ...page.topCustomers].sort((a,b) => b.rewardsAvailable-a.rewardsAvailable).slice(0,8);
        }
        cursor = page.nextCursor || "";
      } while (cursor);
      if (id === requestId.current) setOverview(combined);
    })().catch(() => {
      if (id === requestId.current) setOverviewError("No pudimos actualizar el resumen. Puedes seguir gestionando tus programas y reintentar.");
    }).finally(() => { if (id === requestId.current) setOverviewLoading(false); });
    try {
      try {
        const data = await callLoyaltyFunction("getBusinessLoyaltyPrograms");
        if (id !== requestId.current) return;
        const list = Array.isArray(data.programs) ? data.programs.map((p) => normalizeProgram(p.id, p)) : [];
        setPrograms(list);
        return;
      } catch (callableError) {
        console.warn("getBusinessLoyaltyPrograms callable failed, trying Firestore read fallback", callableError);
      }

      const ref = collection(db, "businesses", businessId, "loyaltyPrograms");
      let snap;
      try {
        snap = await getDocs(query(ref, orderBy("updatedAt", "desc")));
      } catch (_) {
        snap = await getDocs(ref);
      }
      const list = snap.docs.map((d) => normalizeProgram(d.id, d.data() || {}));
      if (id === requestId.current) setPrograms(list);
    } catch (e) {
      console.error("load loyalty programs failed", e);
      if (id === requestId.current) setError("No pudimos cargar los programas de fidelidad.");
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  };

  useEffect(() => {
    setPrograms([]);
    setOverview(null);
    loadPrograms();
    return () => { requestId.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const openNew = () => {
    setEditingId(null);
    setDraft(defaultDraft());
    setDraftOpen(true);
  };

  const openEdit = (program) => {
    setEditingId(program.id);
    setDraft({
      name: program.name || "",
      description: program.description || "",
      type: program.type || "stamp",
      stampsRequired: String(program.stampsRequired || 5),
      stampsPerPaidBooking: String(program.stampsPerPaidBooking || 1),
      pointsRequired: String(program.pointsRequired || 100),
      pointsPerDollar: String(program.pointsPerDollar ?? 1),
      pointsPerPaidBooking: String(program.pointsPerPaidBooking ?? 0),
      pointManualStep: String(program.pointManualStep || 10),
      rewardLabel: program.rewardLabel || "",
      eligibleMode: program.eligibleMode || "all",
      eligibleServiceIds: Array.isArray(program.eligibleServiceIds) ? program.eligibleServiceIds : [],
      active: program.active !== false,
    });
    setDraftOpen(true);
  };

  const toggleService = (serviceId) => {
    setDraft((prev) => {
      const current = new Set(Array.isArray(prev.eligibleServiceIds) ? prev.eligibleServiceIds : []);
      if (current.has(serviceId)) current.delete(serviceId);
      else current.add(serviceId);
      return { ...prev, eligibleServiceIds: Array.from(current) };
    });
  };

  const setProgramType = (type) => {
    setDraft((prev) => ({
      ...prev,
      type,
      pointsPerDollar: type === "points" ? (prev.pointsPerDollar || "1") : prev.pointsPerDollar,
      stampsPerPaidBooking: type === "stamp" ? (prev.stampsPerPaidBooking || "1") : prev.stampsPerPaidBooking,
    }));
  };

  const pointsEarnRule = Number(draft.pointsPerPaidBooking || 0) > 0 && Number(draft.pointsPerDollar || 0) <= 0 ? "per_booking" : "per_dollar";
  const setPointsEarnRule = (rule) => {
    setDraft((prev) => ({
      ...prev,
      pointsPerDollar: rule === "per_dollar" ? (Number(prev.pointsPerDollar || 0) > 0 ? prev.pointsPerDollar : "1") : "0",
      pointsPerPaidBooking: rule === "per_booking" ? (Number(prev.pointsPerPaidBooking || 0) > 0 ? prev.pointsPerPaidBooking : "10") : "0",
    }));
  };

  const saveProgram = async () => {
    if (!db || !businessId) return;
    const name = clean(draft.name);
    const rewardLabel = clean(draft.rewardLabel);
    if (!name) {
      setError("Ponle nombre al programa de fidelidad.");
      return;
    }
    if (!rewardLabel) {
      setError("Define qué recibe el cliente al completar la tarjeta.");
      return;
    }
    const type = draft.type === "points" ? "points" : "stamp";
    const stampsRequired = Math.max(1, asNumber(draft.stampsRequired, 5));
    const stampsPerPaidBooking = Math.max(1, asNumber(draft.stampsPerPaidBooking, 1));
    const pointsRequired = Math.max(1, asNumber(draft.pointsRequired, 100));
    const pointsPerDollar = Math.max(0, asNumber(draft.pointsPerDollar, 1));
    const pointsPerPaidBooking = Math.max(0, asNumber(draft.pointsPerPaidBooking, 0));
    const pointManualStep = Math.max(1, asNumber(draft.pointManualStep, 10));
    const eligibleMode = draft.eligibleMode === "selected" ? "selected" : "all";
    const eligibleServiceIds = eligibleMode === "selected" ? (Array.isArray(draft.eligibleServiceIds) ? draft.eligibleServiceIds.map(String).filter(Boolean) : []) : [];
    if (eligibleMode === "selected" && !eligibleServiceIds.length) {
      setError("Selecciona al menos un servicio elegible o cambia a todos los servicios.");
      return;
    }

    setSaving(true);
    setError("");
    const payload = {
      programId: editingId || null,
      program: {
        name,
        description: clean(draft.description),
        type,
        programType: type,
        loyaltyType: type,
        active: draft.active !== false,
        stampsRequired,
        stampsPerPaidBooking,
        pointsRequired,
        pointsPerDollar,
        pointsPerPaidBooking,
        pointManualStep,
        eligibleMode,
        eligibleServiceIds,
        rewardType: "custom",
        rewardLabel,
        earnTrigger: "payment_paid",
        redeemMode: "manual",
        businessId,
        businessName: clean(businessDoc?.name || businessDoc?.businessName || ""),
      },
    };

    try {
      await callLoyaltyFunction("saveBusinessLoyaltyProgram", payload);
      setDraftOpen(false);
      setEditingId(null);
      setDraft(defaultDraft());
      await loadPrograms();
    } catch (e) {
      console.error("save loyalty program failed", e);
      const detail = e && (e.code || e.message) ? ` (${e.code || e.message})` : "";
      setError(`No pudimos guardar el programa. Intenta de nuevo.${detail}`);
    } finally {
      setSaving(false);
    }
  };

  const toggleProgramActive = async (program) => {
    if (!db || !businessId || !program?.id) return;
    setSaving(true);
    setError("");
    try {
      await callLoyaltyFunction("saveBusinessLoyaltyProgram", {
        programId: program.id,
        program: { ...program, active: program.active === false },
      });
      await loadPrograms();
    } catch (e) {
      console.error("toggle loyalty program failed", e);
      setError("No pudimos actualizar el estado del programa.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProgram = async (program) => {
    if (!db || !businessId || !program?.id) return;
    const ok = window.confirm(`¿Eliminar "${program.name}"? Esto no borra el historial futuro, pero quita la regla de fidelidad.`);
    if (!ok) return;
    setSaving(true);
    setError("");
    try {
      await callLoyaltyFunction("deleteBusinessLoyaltyProgram", { programId: program.id });
      await loadPrograms();
    } catch (e) {
      console.error("delete loyalty program failed", e);
      setError("No pudimos eliminar el programa.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="loyalty-shell loyalty-v2">
      <style>{styles}</style>
      <style>{`
        .loyalty-v2 .loyalty-tip-card{display:none}
        .loyalty-v2 .loyalty-program-footer{flex-wrap:wrap}
        @media(max-width:680px){
          .loyalty-v2 .loyalty-metrics{grid-template-columns:1fr 1fr;gap:8px}
          .loyalty-v2 .loyalty-metric-card{min-height:76px;gap:8px;padding:12px}
          .loyalty-v2 .loyalty-metric-icon{display:none}
          .loyalty-v2 .loyalty-insight-microgrid{grid-template-columns:repeat(3,minmax(0,1fr));gap:6px}
          .loyalty-v2 .loyalty-insight-microgrid div{padding:9px}
          .loyalty-v2 .loyalty-program-grid{grid-template-columns:1fr 1fr;gap:12px}
          .loyalty-v2 .loyalty-program-grid>div:last-child{grid-column:1/-1}
          .loyalty-v2 .loyalty-program-actions{flex-wrap:wrap}
          .loyalty-v2 .loyalty-program-actions button{min-height:44px}
          .loyalty-v2 .loyalty-toolbar{grid-template-columns:1fr auto}
          .loyalty-v2 .loyalty-toolbar>label{grid-column:1/-1}
          .loyalty-v2 .loyalty-sheet{height:100dvh}
        }
      `}</style>

      <div className="loyalty-dashboard-row">
        <div className="loyalty-metrics">
          <MetricCard icon="star" value={loading && !programs.length ? "—" : fmtMetric(programs.length)} label={t("Programas creados")} />
          <MetricCard icon="chart" value={loading && !programs.length ? "—" : fmtMetric(activePrograms)} label={t("Activos")} />
          <MetricCard icon="users" value={overview ? fmtMetric(overviewSnapshot.customersWithLoyalty) : "—"} label={t("Clientes con fidelidad")} />
          <MetricCard icon="gift" value={overview ? fmtMetric(overviewSnapshot.totalRewardsAvailable) : "—"} label={t("Recompensas pendientes")} />
        </div>
        <button type="button" className="loyalty-primary loyalty-new-btn loyalty-new-inline" onClick={openNew}>
          <Icon name="plus" />{t("Nuevo programa")}</button>
      </div>

      <div className="loyalty-toolbar">
        <label>
          <Icon name="search" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Buscar programas...")} />
        </label>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">{t("Todos los estados")}</option>
          <option value="active">{t("Activos")}</option>
          <option value="paused">{t("Pausados")}</option>
          <option value="points">{t("Solo puntos")}</option>
          <option value="stamp">{t("Solo sellos")}</option>
        </select>
        <button type="button" className="loyalty-secondary" onClick={loadPrograms} disabled={loading || overviewLoading || saving}>{loading ? "Actualizando..." : t("Actualizar")}</button>
      </div>

      {error ? <div className="loyalty-error" role="alert">{error}</div> : null}
      {overviewLoading && <p role="status">{t("Actualizando el resumen…")}</p>}
      {overviewError && <p role="status">{overviewError}</p>}

      {!loading && !programs.length ? <EmptyState onCreate={openNew} /> : null}

      <div className="loyalty-programs">
        {visiblePrograms.map((program) => {
          const serviceCount = program.eligibleMode === "selected" ? program.eligibleServiceIds.length : safeServices(services).length;
          return (
            <article key={program.id} className="loyalty-program-card">
              <div className="loyalty-program-main">
                <div className="loyalty-program-icon"><Icon name={program.type === "points" ? "diamond" : "star"} /></div>
                <div className="loyalty-program-content">
                  <div className="loyalty-program-title-row">
                    <h3>{program.name}</h3>
                    <span className={`loyalty-pill ${program.active ? "" : "is-off"}`}>{program.active ? t("Activo") : t("Pausado")}</span>
                  </div>
                  <p>{program.description || programSummary(program, services, currency)}</p>
                  <div className="loyalty-program-grid">
                    <div><span>{t("Gana")}</span><strong>{programEarnText(program, currency)}</strong></div>
                    <div><span>{t("Meta")}</span><strong>{programGoalText(program)}</strong></div>
                    <div><span>{t("Recompensa")}</span><strong>{program.rewardLabel}</strong></div>
                  </div>
                  <div className="loyalty-program-footer">
                    <span><Icon name="users" /> {serviceCount || t("Todos")} {program.eligibleMode === "selected" ? "servicios" : "servicios"}</span>
                    <span><Icon name="redeem" />{" " + t("QR temporal + confirmación")}</span>
                    <span><Icon name="gift" /> {program.type === "points" ? t("Puntos") : t("Sellos")}</span>
                  </div>
                </div>
              </div>
              <div className="loyalty-program-actions">

                <button type="button" className="loyalty-secondary" onClick={() => openEdit(program)}>{t("Editar")}</button>
                <button type="button" className={program.active ? "loyalty-ghost" : "loyalty-primary"} onClick={() => toggleProgramActive(program)} disabled={saving}>{program.active ? "Pausar" : "Activar"}</button>
                <button type="button" className="loyalty-danger" onClick={() => deleteProgram(program)} disabled={saving}>{t("Eliminar")}</button>
              </div>
            </article>
          );
        })}
      </div>

      {overview ? (
        <div className="loyalty-insights-grid">
          <div className="loyalty-insight-card">
            <div className="loyalty-insight-head">
              <span><Icon name="chart" /></span>
              <div>
                <strong>{t("Últimos 30 días")}</strong>
                <small>{fmtMetric(overviewSnapshot.earned30d || 0)}{" " + t("acumulaciones ·") + " "}{fmtMetric(overviewSnapshot.redeemed30d || 0)}{" " + t("canjes")}</small>
              </div>
            </div>
            <div className="loyalty-insight-microgrid">
              <div><b>{fmtMetric(overviewSnapshot.totalStampsBalance || 0)}</b><span>{t("Sellos activos")}</span></div>
              <div><b>{fmtMetric(overviewSnapshot.totalPointsBalance || 0)}</b><span>{t("Puntos activos")}</span></div>
              <div><b>{fmtMetric(overviewSnapshot.lifetimeRewardsRedeemed || 0)}</b><span>{t("Canjes históricos")}</span></div>
            </div>
          </div>

          <div className="loyalty-activity-card">
            <div className="loyalty-activity-head">
              <strong>{t("Última actividad")}</strong>
              <small>{recentEvents.length ? "Actualizado por reservas/canjes" : "Sin movimientos todavía"}</small>
            </div>
            {recentEvents.length ? recentEvents.slice(0, 4).map((event) => <RecentLoyaltyEvent key={event.id} event={event} />) : (
              <p className="loyalty-activity-empty">{t("Cuando un cliente gane sellos/puntos o canjee una recompensa, aparecerá aquí.")}</p>
            )}
          </div>
        </div>
      ) : null}

      {programs.length ? (
        <div className="loyalty-tip-card">
          <span><Icon name="trophy" /></span>
          <div>
            <strong>{topCustomers.length ? "Clientes listos para seguimiento" : "Fidelidad lista para operar"}</strong>
            <p>{topCustomers.length ? `${topCustomers[0].name} tiene ${topCustomers[0].rewardsAvailable || 0} recompensa${Number(topCustomers[0].rewardsAvailable || 0) === 1 ? "" : "s"} disponible${Number(topCustomers[0].rewardsAvailable || 0) === 1 ? "" : "s"}.` : "Las reservas pagadas ya acumulan y los canjes quedan registrados en historial."}</p>
          </div>
          <button type="button" className="loyalty-secondary" onClick={loadPrograms} disabled={loading || overviewLoading || saving}>{t("Actualizar")}</button>
        </div>
      ) : null}

      {draftOpen ? (
        <LoyaltySideSheet onClose={() => setDraftOpen(false)}>
          <div className="loyalty-sheet-head">
            <div className="loyalty-sheet-title">
              <span><Icon name="gift" /></span>
              <div>
                <h3>{editingId ? "Editar programa de fidelidad" : "Nuevo programa de fidelidad"}</h3>
                <p>{editingId ? "Actualiza la regla que premia a tus clientes." : "Crea un programa para premiar la lealtad de tus clientes."}</p>
              </div>
            </div>
            <button type="button" className="loyalty-close" onClick={() => setDraftOpen(false)} aria-label={t("Cerrar")}><Icon name="close" /></button>
          </div>

          <div className="loyalty-sheet-body">
            <Field label={t("Nombre del programa")} help="Será visible para ti y tu equipo.">
              <input value={draft.name} onChange={(e) => setDraft((p) => ({ ...p, name: e.target.value }))} placeholder={t("Ej. Manicure Lovers")} />
            </Field>

            <div className="loyalty-type-block">
              <span>{t("Tipo de programa")}</span>
              <div className="loyalty-type-grid">
                <button type="button" className={`loyalty-type-card ${draft.type === "points" ? "active" : ""}`} onClick={() => setProgramType("points")}>
                  <Icon name="points" />
                  <div><strong>{t("Puntos")}</strong><em>{t("Los clientes acumulan puntos por cada compra.")}</em></div>
                </button>
                <button type="button" className={`loyalty-type-card ${draft.type !== "points" ? "active" : ""}`} onClick={() => setProgramType("stamp")}>
                  <Icon name="star" />
                  <div><strong>{t("Sellos")}</strong><em>{t("Los clientes acumulan sellos por cada compra.")}</em></div>
                </button>
              </div>
            </div>

            <div className="loyalty-fields-two">
              {draft.type === "points" ? (
                <>
                  <Field label={t("Regla para ganar")}>
                    <select value={pointsEarnRule} onChange={(e) => setPointsEarnRule(e.target.value)}>
                      <option value="per_dollar">{t("Puntos por cada {unit} pagado", {unit: currencyUnit})}</option>
                      <option value="per_booking">{t("Puntos fijos por reserva completada y pagada")}</option>
                    </select>
                  </Field>
                  <Field label={t("Cantidad")} help="Cantidad que obtienen por cada acción.">
                    <input type="number" min="0" step="0.1" value={pointsEarnRule === "per_dollar" ? draft.pointsPerDollar : draft.pointsPerPaidBooking} onChange={(e) => setDraft((p) => pointsEarnRule === "per_dollar" ? { ...p, pointsPerDollar: e.target.value } : { ...p, pointsPerPaidBooking: e.target.value })} placeholder={t("Ej. 1")} />
                  </Field>
                  <Field label={t("Meta para recompensa")} help="Cantidad necesaria para obtener la recompensa.">
                    <input type="number" min="1" value={draft.pointsRequired} onChange={(e) => setDraft((p) => ({ ...p, pointsRequired: e.target.value }))} placeholder={t("Ej. 1000 puntos")} />
                  </Field>
                </>
              ) : (
                <>
                  <Field label={t("Regla para ganar")}>
                    <select value="paid_booking" disabled>
                      <option value="paid_booking">{t("Sellos por reserva completada y pagada")}</option>
                    </select>
                  </Field>
                  <Field label={t("Cantidad")} help="Cantidad que obtienen por cada reserva completada y pagada.">
                    <input type="number" min="1" value={draft.stampsPerPaidBooking} onChange={(e) => setDraft((p) => ({ ...p, stampsPerPaidBooking: e.target.value }))} placeholder={t("Ej. 1")} />
                  </Field>
                  <Field label={t("Meta para recompensa")} help="Cantidad necesaria para obtener la recompensa.">
                    <input type="number" min="1" value={draft.stampsRequired} onChange={(e) => setDraft((p) => ({ ...p, stampsRequired: e.target.value }))} placeholder={t("Ej. 10 sellos")} />
                  </Field>
                </>
              )}
              <Field label={t("Recompensa")} help="Lo que el cliente recibirá al alcanzar la meta.">
                <input value={draft.rewardLabel} onChange={(e) => setDraft((p) => ({ ...p, rewardLabel: e.target.value }))} placeholder={t("Ej. 1 manicure gratis / $5 de descuento")} />
              </Field>
              <Field label={t("Servicios elegibles")} help="¿Para qué servicios aplica este programa?">
                <select value={draft.eligibleMode} onChange={(e) => setDraft((p) => ({ ...p, eligibleMode: e.target.value }))}>
                  <option value="all">{t("Todos los servicios")}</option>
                  <option value="selected">{t("Solo servicios seleccionados")}</option>
                </select>
              </Field>
              <Field label={t("Estado")}>
                <select value={draft.active ? "active" : "inactive"} onChange={(e) => setDraft((p) => ({ ...p, active: e.target.value === "active" }))}>
                  <option value="active">{t("Activo")}</option>
                  <option value="inactive">{t("Pausado")}</option>
                </select>
              </Field>
            </div>

            {draft.eligibleMode === "selected" ? (
              <Field label={t("Selecciona servicios")}>
                <div className="loyalty-service-picker">
                  {serviceOptions.length ? serviceOptions.map((service) => {
                    const selected = Array.isArray(draft.eligibleServiceIds) && draft.eligibleServiceIds.includes(service.id);
                    return (
                      <button key={service.id} type="button" className={`loyalty-service-option ${selected ? "is-on" : ""}`} onClick={() => toggleService(service.id)}>
                        <span>{service.label}</span>
                        <b>{selected ? "✓" : "+"}</b>
                      </button>
                    );
                  }) : <small>{t("No hay servicios cargados todavía. Crea servicios primero o usa “Todos los servicios”.")}</small>}
                </div>
              </Field>
            ) : null}

            <div className="loyalty-insight-card" aria-label={t("Vista previa del programa")}>
              <small>{t("ASÍ LO VERÁ TU CLIENTE")}</small>
              <h3>{draft.rewardLabel || t("Tu próxima recompensa")}</h3>
              <p>{businessDoc.name || t("Tu negocio")}</p>
              <strong>0 / {draft.type === "points" ? draft.pointsRequired : draft.stampsRequired} {draft.type === "points" ? "puntos" : "sellos"}</strong>
              <p>{t("Acumula con reservas completadas y pagadas. Los premios ganados se muestran por separado.")}</p>
            </div>
            <Field label={t("Descripción interna")} help="Opcional. Útil para explicar reglas al equipo.">
              <textarea value={draft.description} onChange={(e) => setDraft((p) => ({ ...p, description: e.target.value }))} placeholder={t("Aplica solo para manicura regular. No incluye diseños especiales.")} />
            </Field>
          </div>

          <div className="loyalty-sheet-footer">
            <button type="button" className="loyalty-ghost" onClick={() => setDraftOpen(false)}>{t("Cancelar")}</button>
            <button type="button" className="loyalty-primary" onClick={saveProgram} disabled={saving}>{saving ? t("Guardando...") : editingId ? "Guardar cambios" : t("Crear programa")}</button>
          </div>
        </LoyaltySideSheet>
      ) : null}
    </section>
  );
}


const styles = `
  .loyalty-shell.loyalty-v2{display:grid;gap:14px;padding:0;width:100%;max-width:100%;}
  .loyalty-dashboard-row{display:grid;grid-template-columns:minmax(0,1fr) auto;gap:12px;align-items:stretch;}
  .loyalty-primary,.loyalty-secondary,.loyalty-ghost,.loyalty-danger{height:40px;border-radius:12px;padding:0 14px;font-size:13px;font-weight:900;display:inline-flex;align-items:center;justify-content:center;gap:8px;cursor:pointer;transition:all .18s ease;}
  .loyalty-primary{border:0;background:linear-gradient(135deg,#00675f,#0b7a70);color:#fff;box-shadow:0 10px 22px rgba(0,103,95,.14);}
  .loyalty-secondary{border:1px solid rgba(226,232,240,.95);background:#fff;color:#0f172a;box-shadow:0 6px 18px rgba(15,23,42,.035);}
  .loyalty-ghost{border:1px solid rgba(15,118,110,.16);background:#effcf9;color:#0f766e;}
  .loyalty-danger{border:1px solid rgba(254,202,202,.9);background:#fff7f7;color:#b91c1c;}
  .loyalty-primary:hover{transform:translateY(-1px);box-shadow:0 14px 26px rgba(0,103,95,.18);}
  .loyalty-secondary:hover,.loyalty-ghost:hover,.loyalty-danger:hover{transform:translateY(-1px);}
  .loyalty-primary:disabled,.loyalty-secondary:disabled,.loyalty-ghost:disabled,.loyalty-danger:disabled{opacity:.62;cursor:not-allowed;transform:none;}
  .loyalty-new-btn{height:42px;border-radius:14px;}
  .loyalty-new-inline{min-height:80px;min-width:185px;border-radius:18px;padding:0 18px;align-self:stretch;font-size:13px;}
  .loyalty-metrics{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;}
  .loyalty-metric-card{border:1px solid rgba(226,232,240,.9);background:rgba(255,255,255,.96);border-radius:18px;padding:14px 16px;box-shadow:0 10px 28px rgba(15,23,42,.04);display:flex;align-items:center;gap:12px;min-height:82px;}
  .loyalty-metric-icon{width:40px;height:40px;border-radius:14px;background:#ecfdf5;color:#007467;display:grid;place-items:center;flex:0 0 auto;}
  .loyalty-metric-card strong{display:block;font-size:22px;letter-spacing:-.04em;color:#0f172a;font-weight:950;line-height:1;}
  .loyalty-metric-card em{display:block;margin-top:6px;color:#64748b;font-size:11px;font-style:normal;font-weight:850;}
  .loyalty-toolbar{display:grid;grid-template-columns:minmax(0,1fr) 220px auto;gap:10px;align-items:center;}
  .loyalty-toolbar label{height:42px;border:1px solid #e2e8f0;background:#fff;border-radius:14px;display:flex;align-items:center;gap:8px;padding:0 12px;color:#94a3b8;box-shadow:0 8px 18px rgba(15,23,42,.03);}
  .loyalty-toolbar input{border:0;outline:0;background:transparent;width:100%;height:100%;font-size:13px;font-weight:760;color:#0f172a;}
  .loyalty-toolbar select{height:42px;border:1px solid #e2e8f0;background:#fff;border-radius:14px;padding:0 12px;color:#0f172a;font-size:13px;font-weight:850;outline:0;box-shadow:0 8px 18px rgba(15,23,42,.03);}
  .loyalty-error{border:1px solid #fecaca;background:#fff7f7;color:#991b1b;border-radius:14px;padding:10px 12px;font-size:12px;font-weight:850;}
  .loyalty-empty{border:1px dashed rgba(0,103,95,.22);background:#fff;border-radius:20px;padding:28px 18px;text-align:center;display:grid;place-items:center;gap:8px;box-shadow:0 12px 30px rgba(15,23,42,.035);}
  .loyalty-empty-icon{width:50px;height:50px;border-radius:16px;background:#ecfdf5;color:#007467;display:grid;place-items:center;}
  .loyalty-empty h3{margin:4px 0 0;color:#0f172a;font-size:20px;font-weight:950;letter-spacing:-.035em;}
  .loyalty-empty p{margin:0;max-width:560px;color:#64748b;font-size:13px;line-height:1.45;font-weight:720;}
  .loyalty-empty button{margin-top:8px;height:40px;border:0;border-radius:13px;background:#00675f;color:#fff;padding:0 16px;font-weight:950;cursor:pointer;display:inline-flex;align-items:center;gap:8px;}
  .loyalty-programs{display:grid;gap:12px;}
  .loyalty-program-card{border:1px solid rgba(226,232,240,.92);background:rgba(255,255,255,.97);border-radius:20px;padding:16px 18px;box-shadow:0 12px 30px rgba(15,23,42,.04);display:grid;grid-template-columns:minmax(0,1fr) auto;gap:14px;align-items:start;}
  .loyalty-program-main{display:flex;gap:14px;min-width:0;}
  .loyalty-program-icon{width:52px;height:52px;border-radius:18px;background:#dff8ee;color:#007467;display:grid;place-items:center;flex:0 0 auto;}
  .loyalty-program-content{min-width:0;display:grid;gap:10px;width:100%;}
  .loyalty-program-title-row{display:flex;align-items:center;gap:8px;flex-wrap:wrap;}
  .loyalty-program-title-row h3{margin:0;color:#0f172a;font-size:18px;line-height:1.1;font-weight:950;letter-spacing:-.035em;}
  .loyalty-program-content p{margin:0;color:#64748b;font-size:13px;font-weight:760;line-height:1.4;}
  .loyalty-pill{height:24px;border-radius:999px;padding:0 9px;display:inline-flex;align-items:center;justify-content:center;font-size:10px;font-weight:950;border:1px solid #bbf7d0;background:#ecfdf5;color:#047857;white-space:nowrap;}
  .loyalty-pill.is-off{border-color:#e2e8f0;background:#f8fafc;color:#64748b;}
  .loyalty-program-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));border-top:1px solid #eef2f7;border-bottom:1px solid #eef2f7;padding:11px 0;gap:12px;}
  .loyalty-program-grid div{min-width:0;}
  .loyalty-program-grid span{display:block;color:#94a3b8;font-size:10px;font-weight:950;text-transform:uppercase;letter-spacing:.08em;}
  .loyalty-program-grid strong{display:block;margin-top:5px;color:#0f172a;font-size:12px;font-weight:950;line-height:1.28;}
  .loyalty-program-footer{display:flex;align-items:center;gap:16px;flex-wrap:wrap;color:#475569;font-size:11px;font-weight:800;}
  .loyalty-program-footer span{display:inline-flex;align-items:center;gap:6px;}
  .loyalty-program-footer svg{width:14px;height:14px;color:#64748b;}
  .loyalty-program-actions{display:flex;gap:7px;align-items:center;justify-content:flex-end;flex-wrap:wrap;}
  .loyalty-icon-btn{width:40px;height:40px;border-radius:999px;border:1px solid #e2e8f0;background:#fff;color:#0f172a;display:grid;place-items:center;cursor:pointer;}
  .loyalty-tip-card{border:1px solid rgba(0,103,95,.16);background:linear-gradient(135deg,#f4fffb,#fff);border-radius:18px;padding:14px 16px;display:flex;align-items:center;gap:12px;box-shadow:0 10px 24px rgba(15,23,42,.035);}
  .loyalty-tip-card>span{width:42px;height:42px;border-radius:15px;background:#dff8ee;color:#007467;display:grid;place-items:center;flex:0 0 auto;}
  .loyalty-tip-card div{min-width:0;flex:1;}
  .loyalty-tip-card strong{display:block;color:#0f172a;font-size:14px;font-weight:950;}
  .loyalty-tip-card p{margin:3px 0 0;color:#64748b;font-size:12px;font-weight:720;}
  .loyalty-overlay{position:fixed;inset:0;z-index:1000000;background:rgba(15,23,42,.22);backdrop-filter:blur(8px);display:flex;justify-content:flex-end;padding:16px;}
  .loyalty-sheet{width:min(540px,100%);height:calc(100vh - 32px);background:#fff;border:1px solid rgba(226,232,240,.9);border-radius:24px;box-shadow:0 28px 90px rgba(15,23,42,.2);display:grid;grid-template-rows:auto minmax(0,1fr) auto;overflow:hidden;animation:loyaltySheetIn .18s ease-out;}
  @keyframes loyaltySheetIn{from{opacity:0;transform:translateX(24px) scale(.985)}to{opacity:1;transform:translateX(0) scale(1)}}
  .loyalty-sheet-head{padding:20px 22px 16px;border-bottom:1px solid #eef2f7;display:flex;align-items:flex-start;justify-content:space-between;gap:14px;}
  .loyalty-sheet-title{display:flex;gap:14px;align-items:flex-start;}
  .loyalty-sheet-title>span{width:46px;height:46px;border-radius:16px;background:#ecfdf5;color:#007467;display:grid;place-items:center;flex:0 0 auto;}
  .loyalty-sheet-title h3{margin:0;color:#0f172a;font-size:21px;line-height:1.05;font-weight:950;letter-spacing:-.04em;}
  .loyalty-sheet-title p{margin:7px 0 0;color:#64748b;font-size:12px;font-weight:720;line-height:1.42;}
  .loyalty-close{width:40px;height:40px;border:0;border-radius:999px;background:#f8fafc;color:#0f172a;display:grid;place-items:center;cursor:pointer;}
  .loyalty-sheet-body{padding:18px 22px;overflow:auto;display:grid;gap:14px;}
  .loyalty-sheet-footer{padding:14px 22px;border-top:1px solid #eef2f7;display:flex;justify-content:flex-end;gap:10px;background:#fff;}
  .loyalty-field{display:grid;gap:7px;}
  .loyalty-field>span,.loyalty-type-block>span{font-size:12px;font-weight:950;color:#0f172a;}
  .loyalty-field input,.loyalty-field select,.loyalty-field textarea{width:100%;border:1px solid #dbe5ee;border-radius:14px;background:#fff;padding:0 12px;min-height:42px;color:#0f172a;font-size:13px;font-weight:760;outline:none;}
  .loyalty-field textarea{min-height:88px;padding:12px;line-height:1.5;resize:vertical;}
  .loyalty-field input:focus,.loyalty-field select:focus,.loyalty-field textarea:focus{border-color:#0f766e;box-shadow:0 0 0 4px rgba(15,118,110,.10);}
  .loyalty-field small{color:#64748b;font-size:11px;font-weight:720;line-height:1.35;}
  .loyalty-type-block{display:grid;gap:8px;}
  .loyalty-type-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .loyalty-type-card{border:1px solid #e2e8f0;background:#fff;border-radius:16px;min-height:98px;padding:14px;display:flex;gap:11px;text-align:left;cursor:pointer;color:#0f172a;}
  .loyalty-type-card svg{color:#007467;flex:0 0 auto;}
  .loyalty-type-card strong{display:block;color:#0f172a;font-size:13px;font-weight:950;margin-bottom:6px;}
  .loyalty-type-card em{display:block;color:#64748b;font-size:11px;font-style:normal;font-weight:720;line-height:1.4;}
  .loyalty-type-card.active{border-color:rgba(0,103,95,.55);background:#f0fdfa;box-shadow:0 0 0 4px rgba(0,103,95,.06);}
  .loyalty-fields-two{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
  .loyalty-fields-two .loyalty-field:nth-child(3),.loyalty-fields-two .loyalty-field:nth-child(4),.loyalty-fields-two .loyalty-field:nth-child(5){grid-column:1/-1;}
  .loyalty-service-picker{display:grid;gap:8px;border:1px solid #eef2f7;border-radius:16px;padding:10px;background:#f8fafc;max-height:220px;overflow:auto;}
  .loyalty-service-option{border:1px solid #e2e8f0;background:#fff;border-radius:12px;padding:9px 10px;display:flex;align-items:center;justify-content:space-between;gap:10px;cursor:pointer;font-size:12px;font-weight:850;color:#334155;}
  .loyalty-service-option.is-on{border-color:#99f6e4;background:#f0fdfa;color:#00675f;}
  .loyalty-insights-grid{display:grid;grid-template-columns:minmax(0,1fr) minmax(320px,.78fr);gap:12px;align-items:stretch;}
  .loyalty-insight-card,.loyalty-activity-card{border:1px solid rgba(226,232,240,.92);background:rgba(255,255,255,.97);border-radius:20px;padding:16px;box-shadow:0 12px 30px rgba(15,23,42,.04);}
  .loyalty-insight-head{display:flex;align-items:center;gap:12px;margin-bottom:12px;}
  .loyalty-insight-head>span{width:42px;height:42px;border-radius:15px;background:#ecfdf5;color:#007467;display:grid;place-items:center;flex:0 0 auto;}
  .loyalty-insight-head strong,.loyalty-activity-head strong{display:block;color:#0f172a;font-size:14px;font-weight:950;}
  .loyalty-insight-head small,.loyalty-activity-head small{display:block;margin-top:3px;color:#64748b;font-size:11px;font-weight:760;}
  .loyalty-insight-microgrid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;}
  .loyalty-insight-microgrid div{border:1px solid #eef2f7;background:#f8fafc;border-radius:16px;padding:12px;}
  .loyalty-insight-microgrid b{display:block;color:#0f172a;font-size:20px;font-weight:950;letter-spacing:-.035em;}
  .loyalty-insight-microgrid span{display:block;margin-top:4px;color:#64748b;font-size:10.5px;font-weight:900;}
  .loyalty-activity-card{display:grid;gap:8px;}
  .loyalty-activity-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:2px;}
  .loyalty-event-row{display:grid;grid-template-columns:auto minmax(0,1fr) auto;gap:9px;align-items:center;border:1px solid #eef2f7;background:#fff;border-radius:15px;padding:10px;}
  .loyalty-event-row>span{width:34px;height:34px;border-radius:13px;background:#ecfdf5;color:#007467;display:grid;place-items:center;}
  .loyalty-event-row.is-redeem>span{background:#fff7ed;color:#c2410c;}
  .loyalty-event-row.is-reversal>span{background:#f8fafc;color:#64748b;}
  .loyalty-event-row strong{display:block;color:#0f172a;font-size:12px;font-weight:950;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .loyalty-event-row small{display:block;margin-top:3px;color:#64748b;font-size:11px;font-weight:760;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .loyalty-event-row em{font-style:normal;color:#94a3b8;font-size:10.5px;font-weight:900;white-space:nowrap;}
  .loyalty-activity-empty{margin:0;border:1px dashed #cbd5e1;border-radius:15px;padding:13px;color:#64748b;font-size:12px;font-weight:800;line-height:1.35;}
  @media(max-width:1180px){.loyalty-dashboard-row{grid-template-columns:1fr}.loyalty-new-inline{width:max-content;min-height:42px;height:42px;justify-self:end;border-radius:14px;}}
  @media(max-width:980px){.loyalty-metrics,.loyalty-program-grid{grid-template-columns:1fr 1fr}.loyalty-insights-grid{grid-template-columns:1fr}.loyalty-toolbar{grid-template-columns:1fr}.loyalty-program-card{grid-template-columns:1fr}.loyalty-program-actions{justify-content:flex-start}.loyalty-new-btn{width:max-content}}
  @media(max-width:680px){.loyalty-shell.loyalty-v2{gap:12px}.loyalty-insight-microgrid{grid-template-columns:1fr}.loyalty-activity-head{display:grid}.loyalty-event-row{grid-template-columns:auto minmax(0,1fr)}.loyalty-event-row em{grid-column:2;color:#94a3b8}.loyalty-overlay{padding:0}.loyalty-sheet{height:100vh;border-radius:0}.loyalty-metrics,.loyalty-program-grid,.loyalty-type-grid,.loyalty-fields-two{grid-template-columns:1fr}.loyalty-new-inline{width:100%;justify-self:stretch}.loyalty-tip-card{align-items:flex-start}.loyalty-program-main{display:grid;gap:12px}.loyalty-program-card{padding:14px}.loyalty-program-icon{width:46px;height:46px;border-radius:16px}.loyalty-sheet-head,.loyalty-sheet-body,.loyalty-sheet-footer{padding-left:16px;padding-right:16px}.loyalty-metric-card{min-height:72px;padding:12px 14px}.loyalty-toolbar label,.loyalty-toolbar select{height:40px;border-radius:13px}.loyalty-tip-card{padding:12px 14px}}
`;

